import { queryOptions } from "@tanstack/react-query";
import { consumerApi } from "./client";

// The domain is fixed for the page's lifetime, so it isn't part of the key.
export const organizationQueryOptions = queryOptions({
  queryKey: ["organization"] as const,
  queryFn: ({ signal }) => consumerApi.getOrganization(signal),
});

export const tasksQueryOptions = queryOptions({
  queryKey: ["tasks"] as const,
  queryFn: ({ signal }) => consumerApi.getTasks(signal),
});

export const formOptionsQueryOptions = queryOptions({
  queryKey: ["form_options"] as const,
  queryFn: ({ signal }) => consumerApi.getFormOptions(signal),
  staleTime: Infinity,
});

export const profileQueryOptions = queryOptions({
  queryKey: ["profile"] as const,
  queryFn: ({ signal }) => consumerApi.getProfile(signal),
});

export const addressesQueryOptions = queryOptions({
  queryKey: ["addresses"] as const,
  queryFn: ({ signal }) => consumerApi.getAddresses(signal),
});

export const employmentsQueryOptions = queryOptions({
  queryKey: ["employments"] as const,
  queryFn: ({ signal }) => consumerApi.getEmployments(signal),
});

// The Applicant's own sources only; any staff entered are never listed.
export const additionalIncomesQueryOptions = queryOptions({
  queryKey: ["additional_incomes"] as const,
  queryFn: ({ signal }) => consumerApi.getAdditionalIncomes(signal),
});

export const referencesQueryOptions = queryOptions({
  queryKey: ["references"] as const,
  queryFn: ({ signal }) => consumerApi.getReferences(signal),
});

export const identificationCardsQueryOptions = queryOptions({
  queryKey: ["identification_cards"] as const,
  queryFn: ({ signal }) => consumerApi.getIdentificationCards(signal),
});

export const documentsQueryOptions = queryOptions({
  queryKey: ["documents"] as const,
  queryFn: ({ signal }) => consumerApi.getDocuments(signal),
});

export const documentTypesQueryOptions = queryOptions({
  queryKey: ["document_types"] as const,
  queryFn: ({ signal }) =>
    consumerApi.getDocumentTypes(signal).then((data) => data.document_types),
});

export const appointmentsQueryOptions = queryOptions({
  queryKey: ["appointments"] as const,
  queryFn: ({ signal }) => consumerApi.getAppointments(signal),
});

export const locationsQueryOptions = queryOptions({
  queryKey: ["locations"] as const,
  queryFn: ({ signal }) => consumerApi.getLocations(signal),
});

export const locationQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["location", id] as const,
    queryFn: ({ signal }) => consumerApi.getLocation(id, signal),
  });

export const repQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["rep", id] as const,
    queryFn: ({ signal }) => consumerApi.getRep(id, signal),
  });

export const plaidLinksQueryOptions = queryOptions({
  queryKey: ["plaid_links"] as const,
  queryFn: ({ signal }) => consumerApi.getPlaidLinks(signal),
});

export const inviteQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["invite", slug] as const,
    queryFn: ({ signal }) => consumerApi.getInvite(slug, signal),
  });

export const questionSetQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["question_set", id] as const,
    queryFn: ({ signal }) => consumerApi.getQuestionSet(id, signal),
  });

export const vehiclePreferencesQueryOptions = queryOptions({
  queryKey: ["vehicle_preferences"] as const,
  queryFn: ({ signal }) => consumerApi.getVehiclePreferences(signal),
});

// Every Task's vehicle grid shares this prefix, so one invalidation reaches them all.
export const allTaskVehiclesQueryKey = ["task_vehicles"] as const;

export const taskVehiclesQueryOptions = (taskId: string) =>
  queryOptions({
    queryKey: [...allTaskVehiclesQueryKey, taskId] as const,
    queryFn: ({ signal }) => consumerApi.getTaskVehicles(taskId, signal),
  });

// The tracks are valid at every down payment (the payment bounds are computed across the whole
// down-payment span), so they never need refetching mid-session — a slider whose range shifted
// under the thumb would be worse than one that is slightly generous.
export const vehicleBudgetRangeQueryOptions = queryOptions({
  queryKey: ["vehicle_budget_range"] as const,
  queryFn: ({ signal }) => consumerApi.getVehicleBudgetRange(signal),
  staleTime: Number.POSITIVE_INFINITY,
});

export const vehicleOptionsQueryOptions = queryOptions({
  queryKey: ["vehicle_options"] as const,
  queryFn: ({ signal }) => consumerApi.getVehicleOptions(signal),
  staleTime: 5 * 60 * 1000,
});
